import React, { useContext, useEffect, useState } from 'react';
import { Badge, Breadcrumb, Button, OverlayTrigger, Tab, Tabs } from 'react-bootstrap';
import { withTranslation } from 'react-i18next';
import { BiChevronLeft, BiChevronRight, BiListCheck } from 'react-icons/bi';
import { BsFillSquareFill, BsInfoCircle, BsChevronLeft, BsChevronRight } from "react-icons/bs";
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from "react-toastify";
import { EventContext, LoaderContext, PreferenceContext } from '../../../../../App';
import ConfirmationModal from '../../../../../components/ConfirmationModal/ConfirmationModal';
import { EventType, LS_AUTHORITY_KEY, LS_COMPANY_INFO_KEY, LS_SELECTED_MAT_ISSUE_YEAR, MaterialCategoryList, currentYear, startYear } from '../../../../../core/constants/common';
import MaterialIssueService from '../../../../../core/service/MaterialIssueService';
import Utils from '../../../../../core/utilities/Utils';
import '../MaterialityAssessment.scss';
import StackedCircularProgressBar from '../StackedProgressBar';
import MaterialIssuesLibrary from './MaterialIssuesLibrary';
import OmittedIssues from './OmittedIssues';
import SearchBox from './SearchBox';
import SelectedIssues from './SelectedIssues';

const MaterialIssuesTabs = ({ t }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const lsDat = JSON.parse(localStorage.getItem(LS_AUTHORITY_KEY));
    const selectedYear = localStorage.getItem(LS_SELECTED_MAT_ISSUE_YEAR) ?? null
    const companyId = lsDat.profile.company.id;
    const lsCompanyInfo = JSON.parse(localStorage.getItem(LS_COMPANY_INFO_KEY));
    const matType = searchParams.get('matType');
    const [materialityType, setMaterialityType] = useState(lsCompanyInfo.materialityType == 'SINGLE' ? 'Impact Materiality' : 'Double Materiality');
    const yearOptions = [];
    const year = new Date().getFullYear();
    for (let i = currentYear; i >= startYear; i--) {
        yearOptions.push(<option value={i} key={i}>{i}</option>);
    }
    const [filter, setFilter] = useState({
        year: searchParams.get('year') ?? selectedYear ?? `${year}`,
        category: ''
    });
    const { setLoaderDisplay } = useContext(LoaderContext);
    const [statusCount, setStatusCount] = useState({});
    const [selectedData, setSelectedData] = useState([]);
    const [omittedData, setOmittedData] = useState([]);
    const [reload, setReload] = useState('');
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [versionId, setVersionId] = useState(0);
    const tabName = searchParams.get('tab');
    const [viewType, setViewType] = useState(searchParams.get('type') ?? 'edit');
    const [activeTab, setActiveTab] = useState(tabName ?? 'summary');
    const prevVersion = searchParams.get('vId');
    const [percentage, setPercentage] = useState(0);
    const { preference } = useContext(PreferenceContext);
    const primaryLanguage = localStorage.getItem('primaryLanguage');
    const [data, setData] = useState([]); // Data from ChildComponent
    const [filteredData, setFilteredData] = useState([]); // Filtered data
    const companyInfo = JSON.parse(localStorage.getItem(LS_COMPANY_INFO_KEY));
    const [showProceedToSurveyConfirmation, setShowProceedToSurveyConfirmation] = useState(false);
    // const [showPrompt, confirmNavigation, cancelNavigation] = useCallbackPrompt(statusCount?.version?.status !== 'FINALIZED');
    const { setEvent } = useContext(EventContext);

    useEffect(() => {
        // handleClearQueryParams();
        localStorage.setItem(LS_SELECTED_MAT_ISSUE_YEAR, filter.year);
        getAllCounts();
    }, [filter]);

    const getAllCounts = () => {
        setLoaderDisplay(true)
        MaterialIssueService.getMaterialIssuesCount(filter.year, prevVersion ?? versionId, companyId, true,
            companyInfo.materialTopicCategory ? companyInfo.materialTopicCategory : 'GENERAL'
        ).then(res => {
            setLoaderDisplay(false)
            if (res.status !== 200) return;
            setStatusCount(res.data);
            // setVersionId(res.data.version.id ?? 0);
            setSelectedData(res.data.selectedMasterTopicIds ?? []);
            setOmittedData(res.data.omittedMasterTopicIds ?? []);
            calcPercentage(res.data);
        }).catch(err => {
            setLoaderDisplay(false)
        });
    };

    const calcPercentage = (data) => {
        const selected = data.selectedCount;
        const omitted = data.omittedCount;
        const library = data.libraryCount;
        const percentage = ((selected + omitted) / library) * 100
        setPercentage(percentage);
    }

    const getNewDraft = () => {
        setLoaderDisplay(true)
        MaterialIssueService.getMaterialIssuesNewDraft(filter.year, 0, companyId, filter.category, 0,
            companyInfo.materialTopicCategory ? companyInfo.materialTopicCategory : 'GENERAL').then(res => {
                setLoaderDisplay(false)
                if (res.status !== 200) return;
                setActiveTab('library');
                setViewType('edit');
                setStatusCount(res.data);
                setSelectedData(res.data.selectedMasterTopicIds ?? []);
                setOmittedData(res.data.omittedMasterTopicIds ?? []);
            }).catch(err => {
                setLoaderDisplay(false);
            });
    };

    const finalizeIssues = () => {
        setLoaderDisplay(true);
        MaterialIssueService.finalizeForCompanyNew(companyId, filter.year, statusCount.version.id, primaryLanguage ?? 'en',
            companyInfo.materialTopicCategory ? companyInfo.materialTopicCategory : 'GENERAL'
        ).then(res => {
            setLoaderDisplay(false);
            if (res.status !== 200) return;
            // toast.success('Material issue finalized successfully', {
            //     position: toast.POSITION.TOP_RIGHT,
            // });
            setShowProceedToSurveyConfirmation(true);
            getAllCounts();
        }).catch(err => {
            setLoaderDisplay(false);
        });
    };

    const confirmFinalize = (val) => {
        setShowConfirmation(false);
        if (val) {
            if (statusCount.selectedCount + statusCount.omittedCount >= statusCount.libraryCount) {
                finalizeIssues();
            } else {
                toast.warning('Select or Omit all the issues to finalize', {
                    position: toast.POSITION.TOP_RIGHT,
                    autoClose: 2000
                });
            }
        }
    };

    const navigateToSurveys = (val) => {
        setShowProceedToSurveyConfirmation(false);
        if (val) {
            navigate(`/company_admin/dmat/matassessments`);
        } else {
            setActiveTab('summary')
        }
    }

    const gotoVersionList = () => {
        navigate("/company_admin/dmat/issues/versions?year=" + filter.year);
    }

    const makeNewDraft = () => {
        setVersionId(0);
        getNewDraft();
    }

    const handleClearQueryParams = () => {
        navigate(location.pathname, { replace: true });
    };

    const refreshPage = () => {
        setFilter({
            year: selectedYear ?? `${year}`,
            category: ''
        });
        setViewType('edit');
        handleClearQueryParams();
        getAllCounts();
    }

    const renderHeader = () => {
        return (
            <div className='MaterialIssuesCard_head'>
                <div className='MI_headInner List_header 3'>
                    <Breadcrumb className='h5'>
                        <Breadcrumb.Item onClick={() => navigate(`/company_admin/dmat/stats`)}>{t("Materiality.label")}</Breadcrumb.Item>
                        <Breadcrumb.Item className='cursor' active onClick={() => refreshPage()}>{t("Issues.label")}</Breadcrumb.Item>
                        {/* <Breadcrumb.Item active>{statusCount?.version?.name ?? t("New.label")}</Breadcrumb.Item> */}
                    </Breadcrumb>

                    <div className='header-actions'>
                        {
                            (viewType != 'view' && statusCount?.version?.status == 'FINALIZED') &&
                            <span className="badge bg-badge-New rounded-pill badge-custom">{statusCount?.version?.materialityType == 'SINGLE' ? 'Impact Materiality' : 'Double Materiality'}</span>
                        }
                        {
                            (viewType != 'view' && statusCount?.version?.status != 'FINALIZED') &&
                            <span className="badge bg-badge-New rounded-pill" style={{ color: '#424242 !important' }}>{materialityType}</span>
                        }
                        {
                            statusCount?.version?.status == 'DRAFT' && percentage == 100 &&
                            <span className="badge bg-badge-Finalized rounded-pill">{t("Completed.label")}</span>
                        }
                        {
                            statusCount?.version?.status == 'DRAFT' && percentage < 100 &&
                            <span className="badge bg-badge-Inprogress rounded-pill">{t("Inprogress.label")}</span>
                        }
                        {
                            ((statusCount?.version == null) && viewType != 'view') &&
                            <span className="badge bg-badge-New rounded-pill">{t("NotStarted.label")}</span>
                        }
                        {
                            ((statusCount?.version?.status == 'FINALIZED') && viewType != 'view') &&
                            <span className="badge bg-success rounded-pill">{t("Finalized.label")}</span>
                        }
                        {
                            statusCount?.version?.status == 'DRAFT' && percentage == 100 && activeTab != 'summary' &&

                            <Button variant='primary' onClick={() => setShowConfirmation(true)}> {t("Finalize.label")}</Button>
                        }
                        {
                            (viewType == 'view') &&
                            <div className='MIStatusCaheck text-Draft'>
                                <span className="badge bg-badge-New rounded-pill">{matType == 'SINGLE' ? 'Impact Materiality' : 'Double Materiality'}</span> &nbsp;
                                <Badge className='badge bg-badge-Onboarded rounded-pill me-2'>
                                    {t("View.label")}
                                </Badge>
                            </div>
                        }

                        {/* {
                            ((statusCount?.version == null) && viewType != 'view') &&
                            <>
                                <Button variant='primary' className='btn' onClick={() => { makeNewDraft() }}> {t("Start.label")}</Button>
                            </>
                        }
                        {
                            ((statusCount?.version?.status == 'FINALIZED') && viewType != 'view') &&
                            <>
                                <Button variant='primary' className='btn' onClick={() => { makeNewDraft() }}> {t("New.label")}</Button>
                            </>
                        } */}

                        {/* <div className='searchListBox'>
                            <span><BiSearch /></span>
                            <input type='text' className='form-control ' placeholder={t("Search.label")} />
                        </div> */}
                        {activeTab != 'summary' && <SearchBox data={data} onFilteredData={handleFilteredData} />}
                        {
                            activeTab != 'summary' &&
                            <select
                                className='form-select  w-auto'
                                onChange={(e) => { setFilter({ ...filter, category: e.target.value }) }}
                                value={filter.category}>
                                <option value='' key=''>{t('All.label')}</option>
                                {
                                    MaterialCategoryList({ t }).map(c => {
                                        return <option value={c.value} key={c.key}>{c.text}</option>
                                    })
                                }
                            </select>
                        }

                        <select className="form-select  w-auto form-select-sm" value={filter.year}
                            onChange={(e) => {
                                setFilter({ ...filter, year: e.target.value });
                                setEvent({
                                    event: EventType.DOUBLE_MAT_YEAR_CHANGED,
                                    data: { year: e.target.value },
                                });
                                handleClearQueryParams();
                            }}>
                            {yearOptions}
                        </select>
                        <OverlayTrigger
                            placement="top"
                            overlay={Utils.renderTooltip(
                                t('IssuesVersions.label')
                            )}>
                            <Button variant='primary' className='ListCheckBtn btn-sm' onClick={() => gotoVersionList()}><BiListCheck /></Button>
                        </OverlayTrigger>
                        <OverlayTrigger placement="top" overlay={Utils.renderTooltip(t("NavigateBackAndNext.Stakeholders"))}>
                            <Button variant='secondary' className='back-btn btn-sm scorecard-btn' onClick={() => navigate(`/company_admin/dmat/stakeholders`)}>
                                <BsChevronLeft /> <span>{t("Back.label")}</span>
                            </Button>
                        </OverlayTrigger>
                        <OverlayTrigger placement="top" overlay={Utils.renderTooltip(t("NavigateBackAndNext.MatAssessment"))}>
                            <Button variant='secondary' className='next-btn btn-sm scorecard-btn' onClick={() => navigate(`/company_admin/dmat/matassessments`)}>
                                <span>{t("Next.label")}</span> <BsChevronRight />
                            </Button>
                        </OverlayTrigger>
                    </div>
                </div>


            </div>
        );
    };

    // Callback to get filtered data from SearchComponent
    const handleFilteredData = (filteredItems) => {
        setFilteredData(filteredItems); // Update filtered data state
    };

    // Callback to get data from ChildComponent
    const handleDataFromChild = (childData) => {
        setData(childData); // Store the data in ParentComponent state
        setFilteredData(childData); // Initially, filtered data is the same as full data
    };

    const tabChange = (key) => {
        setData([]);
        setFilteredData([])
        setActiveTab(key);
    }

    const getProgressPercentage = () => {
        const totalCount = statusCount?.selectedCount + statusCount?.omittedCount;
        if (totalCount === 0) {
            return '0%';
        }
        const denominator = statusCount?.version?.status == 'FINALIZED'
            ? (statusCount?.finalLibraryCount > 0 ? statusCount?.finalLibraryCount : 1)
            : (statusCount?.libraryCount > 0 ? statusCount?.libraryCount : 1);
        return ((totalCount / denominator) * 100).toFixed(0) + '%';
    };

    return (
        <div className='MaterialityAssessmentIssuesbox'>
            <div className='row m-0'>
                <div className='col-md-12 px-0'>
                    <div className='MaterialIssuesCard'>
                        {renderHeader()}
                        <div className='MaterialIssuesCard_body'>
                            <div className='MaterialIssuesCard_tabCard IssueTab'>
                                <Tabs
                                    activeKey={activeTab}
                                    onSelect={(key) => tabChange(key)}
                                    id="uncontrolled-tab-example"
                                    className="mb-">

                                    <Tab eventKey="summary" title={<> {t('Summary.label')}  </>}>
                                        <div className='row m-0'>
                                            <div className='col-lg-6 p-2'>
                                                <div className='SummaryIssueCard 3'>
                                                    <div className='SummaryIssueCard_head'>
                                                        <div className='SummaryIssueCard_head_in'>
                                                            <h5>{t("SummaryTitle.label")} {filter.year} </h5>
                                                            {
                                                                statusCount?.version?.status == 'DRAFT' && percentage == 100 &&
                                                                <span className="badge bg-badge-Finalized rounded-pill">{t("Completed.label")}</span>
                                                            }
                                                            {
                                                                statusCount?.version?.status == 'DRAFT' && percentage < 100 &&
                                                                <span className="badge bg-badge-Inprogress rounded-pill">{t("Inprogress.label")}</span>
                                                            }
                                                            {
                                                                ((statusCount?.version == null) && viewType != 'view') &&
                                                                <span className="badge bg-badge-New rounded-pill">{t("NotStarted.label")}</span>
                                                            }
                                                            {
                                                                ((statusCount?.version?.status == 'FINALIZED') && viewType != 'view') &&
                                                                <span className="badge bg-success rounded-pill">{t("Finalized.label")}</span>
                                                            }
                                                        </div>

                                                    </div>
                                                    <div className='SummaryIssueCard_body'>
                                                        <div className='SummaryIssueCard_body_in 1 mb-2'>
                                                            <div className='SummaryIssueCard_Prograss'>
                                                                <StackedCircularProgressBar
                                                                    segments={[
                                                                        { value: statusCount?.selectedCount ?? 0, color: '#51b133' },
                                                                        { value: statusCount?.omittedCount ?? 0, color: '#dc3545' },
                                                                    ]}
                                                                    total={statusCount?.version?.status == 'FINALIZED' ? statusCount?.finalLibraryCount : statusCount?.libraryCount}
                                                                    displayTextToOverride={getProgressPercentage()}
                                                                />
                                                            </div>
                                                            <div>
                                                                <ul>
                                                                    <li className='Total'>
                                                                        <span> {t("Total.label")}</span>  {statusCount?.version?.status == 'FINALIZED' ? statusCount?.finalLibraryCount : statusCount?.libraryCount}
                                                                    </li>
                                                                    <li className='Selected'>
                                                                        <span><BsFillSquareFill /> {t("Selected.label")} </span> {statusCount?.selectedCount ?? 0}
                                                                    </li>
                                                                    <li className='Omitted'>
                                                                        <span><BsFillSquareFill /> {t("Omitted.label")}</span> {statusCount?.omittedCount ?? 0}
                                                                    </li>
                                                                    <li className='Pending'>
                                                                        <span><BsFillSquareFill /> {t("Pending.label")}</span> {(statusCount?.version?.status == 'FINALIZED' ? statusCount?.finalLibraryCount : statusCount?.libraryCount) - (statusCount?.selectedCount + statusCount?.omittedCount)}
                                                                    </li>
                                                                    {
                                                                        ((statusCount?.version == null) && viewType != 'view') &&
                                                                        <li className='pt-3'>
                                                                            <Button variant='primary' className='bnt-sm px-4 w-100' onClick={() => setActiveTab('library')}>{t("Start.label")} </Button>
                                                                        </li>
                                                                    }
                                                                    {
                                                                        ((statusCount?.version?.status == 'FINALIZED') && viewType != 'view') &&
                                                                        <li className='pt-3'>
                                                                            <Button variant='primary' className='bnt-sm px-4 w-100' onClick={() => { makeNewDraft() }}>{t("New.label")} </Button>
                                                                        </li>
                                                                    }
                                                                    {
                                                                        statusCount?.version?.status == 'DRAFT' && percentage < 100 &&
                                                                        <li className='pt-3'>
                                                                            <Button variant='primary' className='bnt-sm px-4 w-100' onClick={() => setActiveTab('library')}>{t("Resume.label")} </Button>
                                                                        </li>
                                                                    }
                                                                    {
                                                                        statusCount?.version?.status == 'DRAFT' && percentage >= 100 &&
                                                                        <li className='pt-3'>
                                                                            <Button variant='primary' className='bnt-sm px-4 w-100' onClick={() => setShowConfirmation(true)}>{t("Finalize.label")} </Button>
                                                                        </li>
                                                                    }
                                                                </ul>
                                                            </div>
                                                        </div>
                                                        {
                                                            ((statusCount?.version == null) && viewType != 'view') &&
                                                            <div className='OVInfo_012'>
                                                                <BsInfoCircle />
                                                                {t("SummaryTextNotStartP1.label")} {filter.year} {t("SummaryTextNotStartP2.label")}
                                                            </div>
                                                        }
                                                        {
                                                            ((statusCount?.version?.status == 'FINALIZED') && viewType != 'view') &&
                                                            <div className='OVInfo_012'>
                                                                <BsInfoCircle />

                                                                {t("SummaryTextFinalizeP1.label")} {filter.year}. {t("SummaryTextFinalizeP2.label")}
                                                            </div>
                                                        }
                                                        {
                                                            statusCount?.version?.status == 'DRAFT' && percentage < 100 &&
                                                            <div className='OVInfo_012'>
                                                                <BsInfoCircle />

                                                                {t("SummaryTextInprogressP1.label")} {filter.year} {t("SummaryTextInprogressP2.label")}
                                                            </div>
                                                        }
                                                        {
                                                            statusCount?.version?.status == 'DRAFT' && percentage == 100 &&
                                                            <div className='OVInfo_012'>
                                                                <BsInfoCircle />

                                                                {t("SummaryTextCompleteP1.label")} {filter.year}. {t("SummaryTextCompleteP2.label")}
                                                            </div>
                                                        }
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Tab>
                                    <Tab eventKey="selected" title={<> {t('Selected.label')} <span className='count_issue02'>{statusCount?.selectedCount ?? 0}</span> </>}>
                                        {activeTab === 'selected' && (
                                            <SelectedIssues filter={filter} prevVersion={prevVersion} viewType={viewType}
                                                onSuccess={(e) => getAllCounts()} status={statusCount?.version?.status}
                                                onData={handleDataFromChild}
                                                filteredData={filteredData} matType={matType} />
                                        )}
                                    </Tab>
                                    <Tab eventKey="omitted" title={<> {t('Omitted.label')} <span className='count_issue02'>{statusCount?.omittedCount ?? 0}</span> </>}>
                                        {activeTab === 'omitted' && (
                                            <OmittedIssues filter={filter} prevVersion={prevVersion} viewType={viewType}
                                                onSuccess={(e) => getAllCounts()} status={statusCount?.version?.status}
                                                onData={handleDataFromChild}
                                                filteredData={filteredData} matType={matType} />
                                        )}
                                    </Tab>
                                    {
                                        viewType == 'edit' &&
                                        <Tab eventKey="library" title={<> {t('Library.label')} <span className='count_issue02'>{statusCount?.version?.status == 'FINALIZED' ? statusCount?.finalLibraryCount : statusCount?.libraryCount}</span> </>}>
                                            {activeTab === 'library' && (
                                                <MaterialIssuesLibrary filter={filter} selectedData={selectedData} omittedData={omittedData} viewType={viewType}
                                                    onSuccess={(e) => { getAllCounts(); }} status={statusCount?.version?.status}
                                                    onData={handleDataFromChild}
                                                    filteredData={filteredData} />
                                            )}
                                        </Tab>
                                    }
                                    {/* <Tab eventKey="methodology" title={<> {t('Methodology.label')}  </>}>
                                        <MaterialIssuesMethodology />
                                    </Tab> */}
                                </Tabs>
                            </div>

                        </div>
                    </div>
                </div>
            </div>

            {
                showConfirmation &&
                <ConfirmationModal
                    modalShow={true}
                    messageText={t("FinalizeModal.label")}
                    callbackModal={confirmFinalize}
                    confirmBtn={t("Confirm.label")}
                    CancelBtn={t("Cancel.label")}
                />
            }

            {
                showProceedToSurveyConfirmation &&
                <ConfirmationModal
                    modalShow={true}
                    title={t('ProceedToSurvey.label')}
                    messageText={t("ProceedToSurveyText.label")}
                    callbackModal={navigateToSurveys}
                    confirmBtn={t("Proceed.label")}
                    CancelBtn={t("DoItLater.label")}
                />
            }

            {/* {
                showPrompt && <ConfirmationModal
                    modalShow={showPrompt}
                    messageText={t("NavigateFromIssuesWarning.label")}
                    callbackModal={(yes) => {
                        if (yes) {
                            confirmNavigation();
                        } else {
                            cancelNavigation();
                        }
                    }}
                    confirmBtn={t("Yes.label")}
                    CancelBtn={t("No.label")}
                />
            } */}
        </div >
    )
};

export default withTranslation()(MaterialIssuesTabs);